import { LinkListEditor } from "../../../components/LinkListEditor";
import { normalizeLinks, linksAreValid } from "../../../utils/linkLists";
import React from "react";
import {
  Box,
  Skeleton,
  Stack,
  TablePagination,
  Typography,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Megaphone,
  Plus,
} from "lucide-react";
import { Tabs, TabsContent } from "../../../components/Tabs";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/Card";
import { Button } from "../../../components/Button";
import { EmptyStateMessage } from "../../../components/EmptyStateMessage";
import { UnifiedModal } from "../../../components/UnifiedModal";
import { ConfirmModal } from "../../../components/ConfirmModal";
import { AnnouncementImageField } from "./AnnouncementImageField";
import { getData } from "../../../components/hooks/getData";
import apiClient from "../../../api-client";
import { useTheme } from "../../../theme/useTheme";
import SuccessPopup from "../../../components/SuccessPopup";
import { CustomTextField } from "../../../components/CustomTextField";
import { MarkdownEditor } from "../../../components/MarkdownEditor";
import { AnnouncementDescription } from "./AnnouncementDescription";
import { Label } from "../../../components/Label";
import { AnnouncementDetailModal } from "./AnnouncementDetailModal";
import { TrialDemoAnnouncement } from "./TrialDemoAnnouncement";
import {
  AnnouncementAudienceSection,
  AnnouncementAudienceSummary,
} from "./AnnouncementAudienceFields";
import {
  AGENCY_SCOPE_ALL,
  AGENCY_SCOPE_EXTERNAL,
  AGENCY_SCOPE_MANUAL,
  AGENCY_SCOPE_SERSEO,
  CLIENT_MODE_ALL,
  CLIENT_MODE_NONE,
  CLIENT_MODE_SELECTED,
  buildAgencyAudiencePayload,
  filterClientsByAgencyIds,
  resolveAudienceAgencies,
  resolveAudienceAgencyIds,
} from "./announcementAudience";

const DEFAULT_ANNOUNCEMENTS_PAGE_SIZE = 25;
const STEP_AUDIENCE = "destinatarios";
const STEP_MESSAGE = "mensaje";
const ANNOUNCEMENT_STEPS = [
  { value: STEP_AUDIENCE, label: "Destinatarios" },
  { value: STEP_MESSAGE, label: "Mensaje" },
];
const AUDIENCE_ERROR_KEYS = [
  "all",
  "all_clients",
  "target_agencies",
  "target_agency_type",
  "target_clients",
  "send_to_consultants",
];
// Alto fijo del cuerpo: el del paso más alto, limitado al viewport (scroll interno).
const MODAL_BODY_HEIGHT = "min(790px, calc(90vh - 140px))";

const getErrorStep = (error) => {
  const data = error?.response?.data ?? error?.data;
  if (!data || typeof data !== "object") return STEP_MESSAGE;
  const keys = Object.keys(data);
  if (keys.some((key) => AUDIENCE_ERROR_KEYS.includes(key))) return STEP_AUDIENCE;
  if (keys.includes("non_field_errors") || keys.includes("detail")) {
    const text = JSON.stringify(data[keys[0]]).toLowerCase();
    if (/agenc|client|target/.test(text)) return STEP_AUDIENCE;
  }
  return STEP_MESSAGE;
};

const createEmptyAnnouncementForm = () => ({
  title: "",
  description: "",
  links: [],
  headerImageFile: null,
  existingHeaderImageName: null,
  removeHeaderImage: false,
  bodyImageFile: null,
  existingBodyImageName: null,
  removeBodyImage: false,
  agencyScope: AGENCY_SCOPE_ALL,
  targetAgencies: [],
  sendToConsultants: false,
  clientMode: CLIENT_MODE_NONE,
  targetClients: [],
});

const getAnnouncementIdentifier = (announcement) => {
  if (!announcement) return null;
  return (
    announcement.id ??
    announcement.pk ??
    announcement.announcement_id ??
    announcement.uuid ??
    null
  );
};

const buildClientRecipientsFromAnnouncement = (announcement) => {
  const targetClients = Array.isArray(announcement?.target_clients)
    ? announcement.target_clients.map((client) => ({
      value: client.id,
      label: client.name || `Cliente #${client.id}`,
      agencyId: client.agency_id ?? null,
    }))
    : [];

  if (targetClients.length === 0) {
    return { mode: CLIENT_MODE_NONE, targetClients: [] };
  }

  if (announcement?.all_clients) {
    return { mode: CLIENT_MODE_ALL, targetClients };
  }

  return { mode: CLIENT_MODE_SELECTED, targetClients };
};

const buildFormStateFromAnnouncement = (
  announcement,
  allAvailableAgencies = []
) => {
  if (!announcement) return createEmptyAnnouncementForm();

  // Normalizar el campo 'all' para manejar strings y booleanos
  let sendToAll = false;
  if (
    announcement.all === true ||
    announcement.all === "true" ||
    announcement.all === 1
  ) {
    sendToAll = true;
  }

  // Obtener los destinatarios del anuncio (sin filtrar por 'all')
  const recipients = [];

  if (Array.isArray(announcement.target_agencies_details)) {
    announcement.target_agencies_details.forEach((agency) => {
      recipients.push({
        id: agency.id ?? agency.agency_id ?? agency?.value ?? agency,
        label:
          agency.name ??
          agency.agency_name ??
          agency.title ??
          `Agencia #${agency.id ?? agency}`,
      });
    });
  } else if (Array.isArray(announcement.target_agencies)) {
    announcement.target_agencies.forEach((agency) => {
      if (typeof agency === "object" && agency !== null) {
        recipients.push({
          id: agency.id ?? agency.pk ?? agency.value ?? agency,
          label:
            agency.name ??
            agency.title ??
            `Agencia #${agency.id ?? agency.pk ?? agency}`,
          agencyType: agency.agency_type ?? null,
        });
      } else {
        recipients.push({
          id: agency,
          label: `Agencia #${agency}`,
        });
      }
    });
  }

  // Si no está marcado como 'all' pero tiene agencias,
  // verificar si tiene todas las agencias disponibles
  if (!sendToAll && allAvailableAgencies.length > 0 && recipients.length > 0) {
    const announcementAgencyIds = new Set(recipients.map((r) => String(r.id)));
    const allAgencyIds = new Set(
      allAvailableAgencies.map((a) => String(a.value))
    );

    // Si tiene exactamente las mismas agencias que todas las disponibles
    if (
      announcementAgencyIds.size === allAgencyIds.size &&
      announcementAgencyIds.size > 0 &&
      [...announcementAgencyIds].every((id) => allAgencyIds.has(id))
    ) {
      sendToAll = true;
    }
  }

  const clientRecipients = buildClientRecipientsFromAnnouncement(announcement);
  const agencyType = announcement.target_agency_type;
  let agencyScope = AGENCY_SCOPE_MANUAL;
  if (agencyType === AGENCY_SCOPE_SERSEO || agencyType === AGENCY_SCOPE_EXTERNAL) {
    agencyScope = agencyType;
  } else if (sendToAll) {
    agencyScope = AGENCY_SCOPE_ALL;
  }

  return {
    title: announcement.title || "",
    description: announcement.description || "",
    links: announcement.links || [],
    headerImageFile: null,
    existingHeaderImageName: getAnnouncementImageName(announcement),
    removeHeaderImage: false,
    bodyImageFile: null,
    existingBodyImageName: getAnnouncementBodyImageName(announcement),
    removeBodyImage: false,
    agencyScope,
    targetAgencies:
      agencyScope === AGENCY_SCOPE_MANUAL
        ? recipients.map((agency) => ({
          label: agency.label,
          value: agency.id,
          agencyType: agency.agencyType,
        }))
        : [],
    sendToConsultants: Boolean(announcement.send_to_consultants),
    clientMode: clientRecipients.mode,
    targetClients: clientRecipients.targetClients,
  };
};

const extractErrorMessage = (
  error,
  fallback = "No se pudo completar la acción."
) => {
  if (!error) return fallback;
  if (typeof error === "string") return error;

  const data = error?.response?.data ?? error?.data;
  if (!data) return fallback;

  if (typeof data === "string") return data;
  if (Array.isArray(data)) return data.join(", ");
  if (typeof data.detail === "string") return data.detail;

  const firstKey = Object.keys(data)[0];
  if (firstKey) {
    const value = data[firstKey];
    if (Array.isArray(value)) return value[0];
    if (typeof value === "string") return value;
  }

  return fallback;
};

const normalizeList = (payload) => {
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload)) return payload;
  return [];
};

const getAnnouncementImageUrl = (announcement) => {
  if (!announcement) return null;
  const possibleKeys = [
    "header_image_url",
    "image_url",
    "image",
    "cover_image_url",
    "thumbnail_url",
  ];

  for (const key of possibleKeys) {
    const value = announcement[key];
    if (typeof value === "string" && value.trim()) {
      return value;
    }
  }
  return null;
};

const getAnnouncementImageName = (announcement) => {
  if (!announcement) return null;
  const possibleNameKeys = [
    "header_image_filename",
    "header_image_name",
    "image_name",
    "image_filename",
    "header_image_original_name",
  ];

  for (const key of possibleNameKeys) {
    const value = announcement[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  const imageUrl = getAnnouncementImageUrl(announcement);
  if (!imageUrl) return null;

  try {
    const url = new URL(
      imageUrl,
      typeof window !== "undefined"
        ? window.location.origin
        : "http://localhost"
    );
    const segments = url.pathname.split("/").filter(Boolean);
    if (segments.length > 0) {
      return segments[segments.length - 1];
    }
  } catch {
    const sanitized = imageUrl.split("?")[0];
    const parts = sanitized.split("/").filter(Boolean);
    if (parts.length > 0) {
      return parts[parts.length - 1];
    }
  }

  return null;
};

const getAnnouncementBodyImageUrl = (announcement) => {
  if (!announcement) return null;
  const value = announcement.body_image_url;
  return typeof value === "string" && value.trim() ? value : null;
};

const getAnnouncementBodyImageName = (announcement) => {
  if (!announcement) return null;
  const imageUrl = getAnnouncementBodyImageUrl(announcement);
  if (!imageUrl) return null;

  try {
    const url = new URL(
      imageUrl,
      typeof window !== "undefined"
        ? window.location.origin
        : "http://localhost"
    );
    const segments = url.pathname.split("/").filter(Boolean);
    if (segments.length > 0) {
      return segments[segments.length - 1];
    }
  } catch {
    const sanitized = imageUrl.split("?")[0];
    const parts = sanitized.split("/").filter(Boolean);
    if (parts.length > 0) {
      return parts[parts.length - 1];
    }
  }

  return null;
};

export const AnnouncementsPanel = React.forwardRef(function AnnouncementsPanel(
  {
    currentUser,
    interceptorReady,
    dashboardRefreshKey,
    defaultPageSize,
    maxHeight = "calc(100vh - 275px)",
    headerSpacing,
    forceAdminView = false,
    forceSuperAdminLayout = false,
    noHeaderGap = false,
    cardImageHeight,
    descriptionLines = 2,
  },
  ref
) {
  const { theme, neutrals } = useTheme();
  const [panelRefreshKey, setPanelRefreshKey] = React.useState(0);
  const [agenciesRefreshKey, setAgenciesRefreshKey] = React.useState(0);
  const [announcements, setAnnouncements] = React.useState([]);
  const [announcementPage, setAnnouncementPage] = React.useState(0);
  const [announcementPageSize, setAnnouncementPageSize] = React.useState(
    defaultPageSize || DEFAULT_ANNOUNCEMENTS_PAGE_SIZE
  );
  const [announcementsTotalCount, setAnnouncementsTotalCount] =
    React.useState(0);
  const [selectedAnnouncement, setSelectedAnnouncement] = React.useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = React.useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);
  const [createForm, setCreateForm] = React.useState(
    createEmptyAnnouncementForm
  );
  const [formError, setFormError] = React.useState(null);
  const [createStep, setCreateStep] = React.useState(STEP_AUDIENCE);
  const [createErrorStep, setCreateErrorStep] = React.useState(STEP_AUDIENCE);
  const [editStep, setEditStep] = React.useState(STEP_AUDIENCE);
  const [editErrorStep, setEditErrorStep] = React.useState(STEP_AUDIENCE);

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [deleteState, setDeleteState] = React.useState({
    isDeleting: false,
    error: null,
  });
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [editForm, setEditForm] = React.useState(createEmptyAnnouncementForm);
  const [editFormError, setEditFormError] = React.useState(null);

  const [isUpdatingAnnouncement, setIsUpdatingAnnouncement] =
    React.useState(false);
  const [announcementBeingEdited, setAnnouncementBeingEdited] =
    React.useState(null);
  const [successPopup, setSuccessPopup] = React.useState({
    isOpen: false,
    message: "",
    error: false,
  });
  const pendingAnnouncementSelectionRef = React.useRef(null);
  const announcementsContainerRef = React.useRef(null);
  const announcementRefs = React.useRef(new Map());
  const [highlightedAnnouncementId, setHighlightedAnnouncementId] =
    React.useState(null);
  const [searchInput, setSearchInput] = React.useState("");
  const [searchTerm, setSearchTerm] = React.useState("");
  const searchDebounceTimerRef = React.useRef(null);
  const lastSearchRef = React.useRef("");
  const [searchRefreshKey, setSearchRefreshKey] = React.useState(0);
  const highlightTimeoutRef = React.useRef(null);

  const isSuperAdmin = currentUser?.role === "superadmin";
  const isAgencyAdmin = currentUser?.role === "agency_admin";
  const isConsultant = currentUser?.role === "consultant";
  const isClient = currentUser?.role === "client";
  const isAgencyScopedUser = isAgencyAdmin || isConsultant;
  // Roles that only read announcements (agency admins, consultants and clients)
  const isAnnouncementReader = isAgencyScopedUser || isClient;
  // For layout purposes, treat reader roles as superadmin when forceSuperAdminLayout is true
  const useAgencyScopedLayout =
    isAnnouncementReader && !forceSuperAdminLayout;
  const announcementImageHeight =
    cardImageHeight ?? (useAgencyScopedLayout || forceAdminView ? 200 : 160);
  const canViewAnnouncements =
    currentUser && (isSuperAdmin || isAnnouncementReader);

  const shouldFetchAnnouncements = Boolean(
    interceptorReady && canViewAnnouncements
  );
  const announcementsEndpoint = shouldFetchAnnouncements
    ? `communication/announcements/?page=${announcementPage + 1
    }&page_size=${announcementPageSize}${searchTerm ? `&search=${searchTerm}` : ""}`
    : null;

  const {
    data: announcementsResponse,
    isLoading: announcementsIsLoading,
    isInitialLoading: announcementsIsInitialLoading,
    error: announcementsError,
  } = getData(
    announcementsEndpoint,
    shouldFetchAnnouncements && !!announcementsEndpoint,
    [
      announcementsEndpoint,
      dashboardRefreshKey,
      panelRefreshKey,
      announcementPage,
      announcementPageSize,
      searchTerm,
      searchRefreshKey,
    ]
  );

  // Debounce para el buscador inline
  React.useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      const trimmed = searchInput.trim();

      // CASO 1: transición búsqueda → vacío (forzar reset)
      if (trimmed === "" && lastSearchRef.current !== "") {
        lastSearchRef.current = "";
        setSearchTerm("");
        setSearchRefreshKey((prev) => prev + 1);
        setAnnouncementPage(0);
        return;
      }

      // CASO 2: búsqueda normal (mínimo 2 caracteres)
      if (trimmed.length >= 2 && trimmed !== lastSearchRef.current) {
        lastSearchRef.current = trimmed;
        setSearchTerm(trimmed);
        setAnnouncementPage(0);
      }
    }, 300);

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchInput]);

  React.useEffect(() => {
    if (!shouldFetchAnnouncements) {
      setAnnouncements([]);
      setAnnouncementsTotalCount(0);
      return;
    }

    if (!announcementsResponse) {
      // Keep previous announcements while the next page is loading to avoid pagination resets
      return;
    }

    const normalizedResults = normalizeList(announcementsResponse);
    setAnnouncements(normalizedResults);
    const totalCount = Array.isArray(announcementsResponse)
      ? normalizedResults.length
      : announcementsResponse.count || normalizedResults.length;
    setAnnouncementsTotalCount(totalCount);
  }, [shouldFetchAnnouncements, announcementsResponse]);

  const scrollAndHighlightAnnouncement = React.useCallback((targetId) => {
    if (!targetId) return false;
    const normalizedId = String(targetId);
    const container = announcementsContainerRef.current;
    const element = announcementRefs.current.get(normalizedId);
    if (!container || !element) return false;

    // First, scroll the main window to bring the container into view
    // This is crucial for Agency Admins where the panel is below the fold
    const containerRect = container.getBoundingClientRect();
    const isContainerVisible =
      containerRect.top >= 0 &&
      containerRect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight);

    if (!isContainerVisible) {
      container.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      // Wait for the window scroll to complete before scrolling within the panel
      setTimeout(() => {
        // Now scroll within the panel to the specific announcement
        const elementTop = element.offsetTop - container.offsetTop;
        const elementBottom = elementTop + element.offsetHeight;
        const visibleTop = container.scrollTop;
        const visibleBottom = visibleTop + container.clientHeight;

        if (elementTop < visibleTop || elementBottom > visibleBottom) {
          container.scrollTo({
            top: Math.max(elementTop - 16, 0),
            behavior: "smooth",
          });
        }
      }, 300); // Small delay to allow window scroll to start
    } else {
      // Container is already visible, just scroll within the panel
      const elementTop = element.offsetTop - container.offsetTop;
      const elementBottom = elementTop + element.offsetHeight;
      const visibleTop = container.scrollTop;
      const visibleBottom = visibleTop + container.clientHeight;

      if (elementTop < visibleTop || elementBottom > visibleBottom) {
        container.scrollTo({
          top: Math.max(elementTop - 16, 0),
          behavior: "smooth",
        });
      }
    }

    setHighlightedAnnouncementId(normalizedId);
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedAnnouncementId(null);
    }, 1500);
    return true;
  }, []);

  React.useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (!pendingAnnouncementSelectionRef.current) return;
    const { id } = pendingAnnouncementSelectionRef.current;
    if (!id) return;
    const handled = scrollAndHighlightAnnouncement(id);
    if (handled) {
      pendingAnnouncementSelectionRef.current = null;
    }
  }, [announcements, scrollAndHighlightAnnouncement]);

  const openAnnouncementFromExternalSource = React.useCallback(
    ({ announcementId, position } = {}) => {
      if (!announcementId) return;
      const normalizedId = String(announcementId);

      const numericPosition =
        typeof position === "number"
          ? position
          : typeof position === "string"
            ? Number(position)
            : null;
      const hasValidPosition =
        Number.isFinite(numericPosition) && numericPosition >= 0;

      if (
        hasValidPosition &&
        announcementPageSize !== DEFAULT_ANNOUNCEMENTS_PAGE_SIZE
      ) {
        setAnnouncementPageSize(DEFAULT_ANNOUNCEMENTS_PAGE_SIZE);
      }

      if (hasValidPosition) {
        const nextPageIndex = Math.floor(
          numericPosition / DEFAULT_ANNOUNCEMENTS_PAGE_SIZE
        );
        pendingAnnouncementSelectionRef.current = { id: normalizedId };
        if (nextPageIndex !== announcementPage) {
          setAnnouncementPage(nextPageIndex);
        } else {
          const handled = scrollAndHighlightAnnouncement(normalizedId);
          if (handled) {
            pendingAnnouncementSelectionRef.current = null;
          }
        }
      } else {
        const handled = scrollAndHighlightAnnouncement(normalizedId);
        if (!handled) {
          pendingAnnouncementSelectionRef.current = { id: normalizedId };
        }
      }
    },
    [announcementPage, announcementPageSize, scrollAndHighlightAnnouncement]
  );

  React.useImperativeHandle(
    ref,
    () => ({
      openAnnouncement: ({ announcementId, position } = {}) =>
        openAnnouncementFromExternalSource({ announcementId, position }),
    }),
    [openAnnouncementFromExternalSource]
  );

  const isAnnouncementsLoading =
    !announcementsResponse &&
    (announcementsIsLoading || announcementsIsInitialLoading);
  const hasAnnouncements = announcements.length > 0;

  React.useEffect(() => {
    const total = announcementsTotalCount || 0;
    if (!total) {
      if (announcementPage !== 0) {
        setAnnouncementPage(0);
      }
      return;
    }

    const maxPage = Math.max(Math.ceil(total / announcementPageSize) - 1, 0);
    if (announcementPage > maxPage) {
      setAnnouncementPage(maxPage);
    }
  }, [announcementsTotalCount, announcementPageSize, announcementPage]);

  React.useEffect(() => {
    setAnnouncementPage(0);
  }, [dashboardRefreshKey, panelRefreshKey]);

  const { data: agenciesData, isLoading: isLoadingAgencies } = getData(
    isSuperAdmin && (isCreateModalOpen || isEditModalOpen) ? "agencies/" : null,
    Boolean(
      interceptorReady && isSuperAdmin && (isCreateModalOpen || isEditModalOpen)
    ),
    [isSuperAdmin, agenciesRefreshKey]
  );

  const agencyOptions = React.useMemo(() => {
    if (!isSuperAdmin || !agenciesData) return [];
    return normalizeList(agenciesData).filter((agency) => !agency.is_archived).map((agency) => ({
      label:
        agency.name || agency.title || `Agencia #${agency.id ?? agency.pk}`,
      value: agency.id ?? agency.pk,
      agencyType: agency.agency_type ?? null,
    }));
  }, [agenciesData, isSuperAdmin]);

  const shouldFetchClientOptions = Boolean(
    interceptorReady && isSuperAdmin && (isCreateModalOpen || isEditModalOpen)
  );
  const { data: clientsData, isLoading: isLoadingClients } = getData(
    shouldFetchClientOptions ? "clients/options/?ordering=name" : null,
    shouldFetchClientOptions,
    [isSuperAdmin, agenciesRefreshKey]
  );

  const clientOptions = React.useMemo(() => {
    if (!isSuperAdmin || !clientsData) return [];
    return normalizeList(clientsData).map((client) => ({
      value: client.id,
      label: client.name || `Cliente #${client.id}`,
      agencyId: client.agency_id ?? null,
      agencyName: client.agency_name || null,
    }));
  }, [clientsData, isSuperAdmin]);

  // Solo se pueden elegir clientes de las agencias destinatarias
  const getClientOptionsForForm = React.useCallback(
    (formState) =>
      filterClientsByAgencyIds(
        clientOptions,
        resolveAudienceAgencyIds(formState, agencyOptions)
      ),
    [clientOptions, agencyOptions]
  );

  const createClientOptions = React.useMemo(
    () => getClientOptionsForForm(createForm),
    [getClientOptionsForForm, createForm]
  );
  const editClientOptions = React.useMemo(
    () => getClientOptionsForForm(editForm),
    [getClientOptionsForForm, editForm]
  );

  const resetCreateForm = React.useCallback(() => {
    setCreateForm(createEmptyAnnouncementForm());
    setFormError(null);
  }, []);

  const handleOpenCreateModal = () => {
    resetCreateForm();
    setCreateStep(STEP_AUDIENCE);
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setFormError(null);
  };

  const handleAnnouncementPageChange = (event, newPage) => {
    setAnnouncementPage(newPage);
  };

  const handleAnnouncementRowsPerPageChange = (event) => {
    const nextPageSize = parseInt(event.target.value, 10);
    setAnnouncementPageSize(nextPageSize);
    setAnnouncementPage(0);
  };

  const triggerPanelRefresh = React.useCallback(() => {
    setPanelRefreshKey((prev) => prev + 1);
  }, []);

  const buildBasePayloadFromForm = (formState) => {
    const payload = {
      title: formState.title.trim(),
      description: formState.description.trim(),
      ...buildAgencyAudiencePayload(formState),
      send_to_consultants: Boolean(formState.sendToConsultants),
    };

    if (formState.clientMode === CLIENT_MODE_ALL) {
      payload.all_clients = true;
    } else if (formState.clientMode === CLIENT_MODE_SELECTED) {
      payload.target_clients = (formState.targetClients || []).map(
        (client) => client.value
      );
    } else {
      payload.target_clients = [];
    }

    payload.links = normalizeLinks(formState.links);

    return payload;
  };

  const getAgencyAudienceError = (formState) => {
    if (!isSuperAdmin) return null;
    if (formState.agencyScope !== AGENCY_SCOPE_ALL) {
      const agencyIds = resolveAudienceAgencyIds(formState, agencyOptions);
      if (agencyIds.size === 0) {
        return formState.agencyScope === AGENCY_SCOPE_MANUAL
          ? "Selecciona al menos una agencia destinataria."
          : "No hay agencias de ese tipo.";
      }
    }
    if (
      formState.clientMode === CLIENT_MODE_SELECTED &&
      (formState.targetClients || []).length === 0
    ) {
      return "Selecciona al menos un cliente o cambia los clientes destinatarios.";
    }
    return null;
  };

  const renderAudienceSection = (formState, setFormState, formClientOptions) => (
    <AnnouncementAudienceSection
      formState={formState}
      setFormState={setFormState}
      agencyOptions={agencyOptions}
      isLoadingAgencies={isLoadingAgencies && agencyOptions.length === 0}
      clientOptions={formClientOptions}
      isLoadingClients={isLoadingClients}
    />
  );

  const renderMessageFields = (formState, setFormState, isEdit) => (
    <Stack spacing={2}>
      <Box>
        <Label>Título</Label>
        <Box sx={{ mt: 1 }}>
          <CustomTextField
            value={formState.title}
            onChange={(event) =>
              setFormState((prev) => ({ ...prev, title: event.target.value }))
            }
            placeholder="Escribe el título del anuncio"
          />
        </Box>
      </Box>

      <Box>
        <Label>Descripción</Label>
        <Box sx={{ mt: 1 }}>
          <MarkdownEditor
            value={formState.description}
            onChange={(description) =>
              setFormState((prev) => ({ ...prev, description }))
            }
            placeholder="Detalla la comunicación que quieres compartir"
            minHeight="120px"
            resizable
            overlayZIndex={100001}
          />
        </Box>
      </Box>

      <LinkListEditor
        label="Enlaces asociados"
        value={formState.links}
        onChange={(links) => setFormState((prev) => ({ ...prev, links }))}
      />

      <Box>
        <Label>Imagen del anuncio, se renderiza en grande (opcional)</Label>
        <Box sx={{ mt: 1 }}>
          <AnnouncementImageField
            file={formState.headerImageFile}
            {...(isEdit && {
              existingFileLabel: formState.existingHeaderImageName,
              existingFileUrl: formState.existingHeaderImageName
                ? getAnnouncementImageUrl(selectedAnnouncement)
                : null,
            })}
            title="Imagen del anuncio"
            onFileSelect={(file) =>
              setFormState((prev) => ({ ...prev, headerImageFile: file }))
            }
            onRemove={() =>
              setFormState((prev) => ({
                ...prev,
                headerImageFile: null,
                ...(isEdit && {
                  existingHeaderImageName: null,
                  removeHeaderImage: true,
                }),
              }))
            }
          />
        </Box>
      </Box>

      <Box>
        <Label>Añadir imagen al cuerpo (opcional)</Label>
        <Box sx={{ mt: 1 }}>
          <AnnouncementImageField
            file={formState.bodyImageFile}
            {...(isEdit && {
              existingFileLabel: formState.existingBodyImageName,
              existingFileUrl: formState.existingBodyImageName
                ? getAnnouncementBodyImageUrl(selectedAnnouncement)
                : null,
            })}
            title="Imagen del cuerpo"
            onFileSelect={(file) =>
              setFormState((prev) => ({ ...prev, bodyImageFile: file }))
            }
            onRemove={() =>
              setFormState((prev) => ({
                ...prev,
                bodyImageFile: null,
                ...(isEdit && {
                  existingBodyImageName: null,
                  removeBodyImage: true,
                }),
              }))
            }
          />
        </Box>
      </Box>
    </Stack>
  );

  const renderAnnouncementModal = ({
    open,
    onClose,
    title,
    formState,
    setFormState,
    formClientOptions,
    error,
    setError,
    errorStep,
    step,
    setStep,
    isBusy,
    onSubmit,
    submitLabel,
    busyLabel,
    isEdit,
  }) => {
    const isStepper = isSuperAdmin;
    const currentStep = isStepper ? step : STEP_MESSAGE;
    const goToStep = (nextStep) => {
      if (currentStep === STEP_AUDIENCE && nextStep === STEP_MESSAGE) {
        const audienceError = getAgencyAudienceError(formState);
        if (audienceError) {
          setError(audienceError);
          return;
        }
      }
      setError(null);
      setStep(nextStep);
    };
    const errorNode =
      error && errorStep === currentStep ? (
        <Typography sx={{ color: theme.palette.error.main, fontSize: "0.875rem" }}>
          {error}
        </Typography>
      ) : null;

    const footer = (
      <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
        {isStepper && currentStep === STEP_MESSAGE ? (
          <Button variant="outline" onClick={() => goToStep(STEP_AUDIENCE)} disabled={isBusy}>
            <ChevronLeft size={16} />
            Anterior
          </Button>
        ) : (
          <Button variant="outline" onClick={onClose} disabled={isBusy}>
            Cancelar
          </Button>
        )}
        {isStepper && currentStep === STEP_AUDIENCE ? (
          <Button onClick={() => goToStep(STEP_MESSAGE)}>
            Siguiente
            <ChevronRight size={16} />
          </Button>
        ) : (
          <Button onClick={onSubmit} disabled={isBusy}>
            {isBusy ? busyLabel : submitLabel}
          </Button>
        )}
      </Box>
    );

    return (
      <UnifiedModal
        open={open}
        onClose={onClose}
        title={title}
        maxWidth="sm"
        footer={footer}
        contentSx={
          isStepper ? { flex: "none", height: MODAL_BODY_HEIGHT } : undefined
        }
      >
        {isStepper ? (
          <Tabs
            tabs={ANNOUNCEMENT_STEPS}
            value={currentStep}
            onValueChange={goToStep}
            contentSx={{ overflow: "visible", mb: 0 }}
          >
            <TabsContent value={STEP_AUDIENCE}>
              <Stack spacing={2}>
                {errorNode}
                {renderAudienceSection(formState, setFormState, formClientOptions)}
              </Stack>
            </TabsContent>
            <TabsContent value={STEP_MESSAGE}>
              <Stack spacing={2}>
                {errorNode}
                <AnnouncementAudienceSummary
                  scope={formState.agencyScope}
                  agencies={resolveAudienceAgencies(formState, agencyOptions)}
                  sendToConsultants={formState.sendToConsultants}
                  clientMode={formState.clientMode}
                  clients={
                    formState.clientMode === CLIENT_MODE_ALL
                      ? formClientOptions
                      : formState.targetClients
                  }
                  onEdit={() => goToStep(STEP_AUDIENCE)}
                />
                {renderMessageFields(formState, setFormState, isEdit)}
              </Stack>
            </TabsContent>
          </Tabs>
        ) : (
          <Stack spacing={2}>
            {errorNode}
            {renderMessageFields(formState, setFormState, isEdit)}
          </Stack>
        )}
      </UnifiedModal>
    );
  };

  const buildImageFormData = ({ headerImageFile, bodyImageFile } = {}) => {
    if (!headerImageFile && !bodyImageFile) return null;
    const formData = new FormData();
    if (headerImageFile) formData.append("header_image", headerImageFile);
    if (bodyImageFile) formData.append("body_image", bodyImageFile);
    return formData;
  };

  const handleCreateAnnouncement = async () => {
    const audienceError = getAgencyAudienceError(createForm);
    if (audienceError) {
      setFormError(audienceError);
      setCreateErrorStep(STEP_AUDIENCE);
      setCreateStep(STEP_AUDIENCE);
      return;
    }
    if (!createForm.title.trim() || !createForm.description.trim()) {
      setFormError("Completa el título y la descripción.");
      setCreateErrorStep(STEP_MESSAGE);
      setCreateStep(STEP_MESSAGE);
      return;
    }
    if (!linksAreValid(createForm.links)) {
      setFormError("Revisa los enlaces del anuncio.");
      setCreateErrorStep(STEP_MESSAGE);
      setCreateStep(STEP_MESSAGE);
      return;
    }

    const payload = buildBasePayloadFromForm(createForm);

    try {
      setIsSubmitting(true);
      setFormError(null);
      const response = await apiClient.post(
        "/communication/announcements/",
        payload
      );
      const createdAnnouncement = response?.data ?? response;

      if (createForm.headerImageFile || createForm.bodyImageFile) {
        const imageFormData = buildImageFormData({
          headerImageFile: createForm.headerImageFile,
          bodyImageFile: createForm.bodyImageFile,
        });
        const createdAnnouncementId =
          getAnnouncementIdentifier(createdAnnouncement);
        if (imageFormData && createdAnnouncementId) {
          await apiClient.patch(
            `/communication/announcements/${createdAnnouncementId}/`,
            imageFormData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
        }
      }
      handleCloseCreateModal();
      triggerPanelRefresh();
      setSuccessPopup({
        isOpen: true,
        message: "Anuncio creado exitosamente",
        error: false,
      });
    } catch (error) {
      const message = extractErrorMessage(
        error,
        "No se pudo crear el anuncio. Intenta nuevamente."
      );

      setFormError(message);
      const failedStep = getErrorStep(error);
      setCreateErrorStep(failedStep);
      setCreateStep(failedStep);
      setSuccessPopup({
        isOpen: true,
        message: message,
        error: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEditModal = () => {
    if (!selectedAnnouncement) return;
    setAnnouncementBeingEdited(selectedAnnouncement);
    setEditForm(
      buildFormStateFromAnnouncement(selectedAnnouncement, agencyOptions)
    );
    setEditFormError(null);
    setEditStep(STEP_AUDIENCE);

    setIsEditModalOpen(true);
    setIsDetailModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditForm(createEmptyAnnouncementForm());
    setEditFormError(null);

    setAnnouncementBeingEdited(null);
    // También cerrar el modal de detalle
    setIsDetailModalOpen(false);
  };

  // Actualizar el formulario de edición cuando las agencias estén disponibles
  React.useEffect(() => {
    if (
      isEditModalOpen &&
      announcementBeingEdited &&
      agencyOptions.length > 0 &&
      !isLoadingAgencies
    ) {
      setEditForm(
        buildFormStateFromAnnouncement(announcementBeingEdited, agencyOptions)
      );
    }
  }, [
    isEditModalOpen,
    announcementBeingEdited,
    agencyOptions,
    isLoadingAgencies,
  ]);

  const handleUpdateAnnouncement = async () => {
    if (!announcementBeingEdited) return;

    const audienceError = getAgencyAudienceError(editForm);
    if (audienceError) {
      setEditFormError(audienceError);
      setEditErrorStep(STEP_AUDIENCE);
      setEditStep(STEP_AUDIENCE);
      return;
    }
    if (!editForm.title.trim() || !editForm.description.trim()) {
      setEditFormError("Completa el título y la descripción.");
      setEditErrorStep(STEP_MESSAGE);
      setEditStep(STEP_MESSAGE);
      return;
    }
    if (!linksAreValid(editForm.links)) {
      setEditFormError("Revisa los enlaces del anuncio.");
      setEditErrorStep(STEP_MESSAGE);
      setEditStep(STEP_MESSAGE);
      return;
    }

    const payload = buildBasePayloadFromForm(editForm);
    if (editForm.removeHeaderImage) payload.header_image = null;
    if (editForm.removeBodyImage) payload.body_image = null;
    const editingAnnouncementId = announcementBeingEdited.id;

    try {
      setIsUpdatingAnnouncement(true);
      setEditFormError(null);
      await apiClient.patch(
        `/communication/announcements/${editingAnnouncementId}/`,
        payload
      );

      if (editForm.headerImageFile || editForm.bodyImageFile) {
        const imageFormData = buildImageFormData({
          headerImageFile: editForm.headerImageFile,
          bodyImageFile: editForm.bodyImageFile,
        });
        if (imageFormData) {
          await apiClient.patch(
            `/communication/announcements/${editingAnnouncementId}/`,
            imageFormData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
        }
      }
      handleCloseEditModal();
      triggerPanelRefresh();
      setSuccessPopup({
        isOpen: true,
        message: "Anuncio actualizado exitosamente",
        error: false,
      });
      // Cerrar también el modal de detalle después de guardar
      setIsDetailModalOpen(false);
    } catch (error) {
      const message = extractErrorMessage(
        error,
        "No se pudo actualizar el anuncio. Intenta nuevamente."
      );

      setEditFormError(message);
      const failedStep = getErrorStep(error);
      setEditErrorStep(failedStep);
      setEditStep(failedStep);
      setSuccessPopup({
        isOpen: true,
        message: message,
        error: true,
      });
    } finally {
      setIsUpdatingAnnouncement(false);
    }
  };

  const handleCloseSuccessPopup = () => {
    setSuccessPopup({ isOpen: false, message: "", error: false });
  };

  const handleDeleteAnnouncement = async () => {
    if (!selectedAnnouncement) return;
    setDeleteState({ isDeleting: true, error: null });

    try {
      await apiClient.delete(
        `/communication/announcements/${selectedAnnouncement.id}/`
      );
      if (
        announcementBeingEdited &&
        announcementBeingEdited.id === selectedAnnouncement.id
      ) {
        handleCloseEditModal();
      }
      setIsDetailModalOpen(false);
      triggerPanelRefresh();
      setSuccessPopup({
        isOpen: true,
        message: "Anuncio eliminado exitosamente",
        error: false,
      });
    } catch (error) {
      const message = extractErrorMessage(
        error,
        "No se pudo eliminar el anuncio."
      );
      setDeleteState({ isDeleting: false, error: message });
      setSuccessPopup({
        isOpen: true,
        message: message,
        error: true,
      });
      return;
    }

    setDeleteState({ isDeleting: false, error: null });
  };

  const renderAnnouncementCard = (announcement) => {
    const announcementIdentifier = getAnnouncementIdentifier(announcement);
    const normalizedId =
      announcementIdentifier !== null ? String(announcementIdentifier) : null;
    const isHighlighted =
      normalizedId && highlightedAnnouncementId === normalizedId;
    const imageUrl = getAnnouncementImageUrl(announcement);
    const fallbackBackground =
      "linear-gradient(135deg, rgba(99,102,241,0.15), rgba(236,72,153,0.15))";

    return (
      <Box
        key={announcement.id}
        ref={(el) => {
          if (!normalizedId) return;
          if (el) {
            announcementRefs.current.set(normalizedId, el);
          } else {
            announcementRefs.current.delete(normalizedId);
          }
        }}
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: "12px",
          border: `1px solid ${neutrals.gray[200]}`,
          backgroundColor: theme.palette.background.paper,
          boxShadow: isHighlighted ? "0 20px 40px rgba(15,23,42,0.25)" : "none",
          overflow: "hidden",
          cursor: "pointer",
          transition: "all 300ms ease",
          position: "relative",
          transform: isHighlighted ? "scale(1.02)" : "scale(1)",
          animation: isHighlighted
            ? "pulse-announcement 2.5s ease-in-out"
            : "none",
          "@keyframes pulse-announcement": {
            "0%, 100%": {
              transform: "scale(1.02)",
              boxShadow: "0 20px 40px rgba(15,23,42,0.25)",
            },
            "50%": {
              transform: "scale(1.03)",
              boxShadow: "0 24px 48px rgba(15,23,42,0.35)",
            },
          },
          "&:hover": {
            boxShadow: isHighlighted
              ? "0 20px 40px rgba(15,23,42,0.25)"
              : "0 4px 12px rgba(15,23,42,0.12)",
          },
        }}
        onClick={() => {
          setSelectedAnnouncement(announcement);
          setIsDetailModalOpen(true);
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: announcementImageHeight,
            backgroundImage: imageUrl ? "none" : fallbackBackground,
            backgroundSize: "cover",
            backgroundPosition: "center",
            overflow: "hidden",
          }}
        >
          {imageUrl && (
            <Box
              component="img"
              src={imageUrl}
              alt={announcement.title || "Anuncio"}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          )}

        </Box>

        <Box
          sx={{
            p: 2,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 0.75,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  fontSize: "1rem",
                  flex: 1,
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {announcement.title}
              </Typography>
            </Box>

            <AnnouncementDescription
              variant="body2"
              sx={{
                margin: 0,
                color: theme.palette.text.secondary,
                lineHeight: 1.4,
                display: "-webkit-box",
                WebkitLineClamp: descriptionLines,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {announcement.description}
            </AnnouncementDescription>
          </Box>

          <Button
            size="sm"
            fullWidth
            onClick={(event) => {
              event.stopPropagation();
              setSelectedAnnouncement(announcement);
              setIsDetailModalOpen(true);
            }}
          >
            Ver más
          </Button>
        </Box>
      </Box>
    );
  };

  const renderCreateCard = () => (
    <Box
      key="create-announcement-card"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        borderRadius: "12px",
        border: `2px dashed ${neutrals.gray[300]}`,
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        minHeight: 260,
        cursor: "pointer",
        transition: "all 200ms ease",
        "&:hover": {
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
          "& .create-icon-circle": {
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
          },
        },
      }}
      onClick={() => {
        setAgenciesRefreshKey((prev) => prev + 1);
        handleOpenCreateModal();
      }}
    >
      <Box
        className="create-icon-circle"
        sx={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          border: `1px dashed ${neutrals.gray[300]}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "border-color 200ms ease",
        }}
      >
        <Plus size={32} />
      </Box>
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, textAlign: "center" }}
      >
        Crear nuevo anuncio
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          textAlign: "center",
          maxWidth: 220,
        }}
      >
        Comparte un anuncio con todas las agencias o con agencias específicas.
      </Typography>
    </Box>
  );

  const cardHeight = maxHeight === "none" ? "auto" : "100%";

  return (
    <>
      <Card
        sx={{
          height: cardHeight,
          maxHeight,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <CardHeader
          sx={{
            pb: "4px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <CardTitle
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: noHeaderGap ? "1.1rem" : undefined,
              }}
            >
              <Megaphone size={noHeaderGap ? 18 : 20} />
              Anuncios
            </CardTitle>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                justifyContent: "flex-end",
                flex: 1,
              }}
            >
              <Box sx={{ width: { xs: "100%", md: "240px" } }}>
                <CustomTextField
                  isSearch
                  placeholder="Buscar anuncios..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </Box>
              {announcementsError && (
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.error.main, fontSize: "13px" }}
                >
                  Error al cargar anuncios
                </Typography>
              )}
              {(useAgencyScopedLayout || forceAdminView) &&
                announcementsTotalCount > 0 && (
                  <TablePagination
                    component="div"
                    count={announcementsTotalCount}
                    page={announcementPage}
                    onPageChange={handleAnnouncementPageChange}
                    rowsPerPage={announcementPageSize}
                    onRowsPerPageChange={handleAnnouncementRowsPerPageChange}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage="Anuncios por página:"
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}-${to} de ${count !== -1 ? count : `más de ${to}`
                      }`
                    }
                    sx={{
                      width: "auto",
                      maxWidth: "100%",
                      overflow: "hidden",
                      "& .MuiTablePagination-toolbar": {
                        px: { xs: 0.5, md: 1 },
                        minHeight: "18px",
                        height: "18px",
                        flexWrap: { xs: "wrap", md: "nowrap" },
                        gap: { xs: 1, md: 0 },
                      },
                      "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                      {
                        fontSize: {
                          xs: "0.75rem",
                          md: "0.875rem",
                        },
                        whiteSpace: "nowrap",
                      },
                      "& .MuiTablePagination-select": {
                        fontSize: { xs: "0.75rem", md: "0.875rem" },
                      },
                    }}
                  />
                )}
            </Box>
          </Box>
        </CardHeader>
        <CardContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            maxHeight: "100%",
            overflow: "hidden",
            pb: useAgencyScopedLayout || forceAdminView ? "24px" : 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              maxHeight: "100%",
            }}
          >
            <Box
              ref={announcementsContainerRef}
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                pr: 0.5,
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: neutrals.gray[100],
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: neutrals.gray[300],
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: neutrals.gray[400],
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    useAgencyScopedLayout || forceAdminView
                      ? {
                        xs: "1fr",
                        sm: "repeat(auto-fill, minmax(280px, 1fr))",
                      }
                      : { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                  gap: { xs: 1.5, md: 2 },
                  alignContent: "start",
                  p: { xs: 0.5, md: 1 },
                }}
              >
                {isSuperAdmin && announcementPage === 0 && renderCreateCard()}
                {announcementPage === 0 && <TrialDemoAnnouncement />}
                {isAnnouncementsLoading ? (
                  Array.from({ length: 6 }).map((_, index) => (
                    <Box
                      key={`announcement-skeleton-${index}`}
                      sx={{
                        borderRadius: "12px",
                        border: `1px solid ${theme.palette.divider}`,
                        overflow: "hidden",
                        backgroundColor: theme.palette.background.paper,
                      }}
                    >
                      <Skeleton variant="rectangular" height={140} />
                      <Box sx={{ p: 2 }}>
                        <Skeleton variant="text" width="80%" height={24} />
                        <Skeleton variant="text" width="60%" height={24} />
                        <Skeleton
                          variant="text"
                          width="100%"
                          height={18}
                          sx={{ mt: 1 }}
                        />
                        <Skeleton
                          variant="rectangular"
                          height={36}
                          sx={{ mt: 2, borderRadius: "8px" }}
                        />
                      </Box>
                    </Box>
                  ))
                ) : hasAnnouncements ? (
                  announcements.map((announcement) =>
                    renderAnnouncementCard(announcement)
                  )
                ) : (
                  <Box sx={{ gridColumn: "1 / -1" }}>
                    <EmptyStateMessage
                      icon={Megaphone}
                      message="No hay anuncios disponibles"
                      sx={{ py: 3 }}
                    />
                  </Box>
                )}
              </Box>
            </Box>

            {!useAgencyScopedLayout &&
              !forceAdminView &&
              announcementsTotalCount > 0 && (
                <TablePagination
                  component="div"
                  count={announcementsTotalCount}
                  page={announcementPage}
                  onPageChange={handleAnnouncementPageChange}
                  rowsPerPage={announcementPageSize}
                  onRowsPerPageChange={handleAnnouncementRowsPerPageChange}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                  }
                  sx={{
                    borderTop: `1px solid ${theme.palette.divider}`,
                    width: "100%",
                    maxWidth: "100%",
                    overflow: "hidden",
                    "& .MuiTablePagination-toolbar": {
                      px: { xs: 1, md: 2 },
                      flexWrap: { xs: "wrap", md: "nowrap" },
                      gap: { xs: 1, md: 0 },
                      width: "100%",
                      maxWidth: "100%",
                      overflow: "hidden",
                    },
                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                    {
                      fontSize: { xs: "0.75rem", md: "0.875rem" },
                      whiteSpace: { xs: "nowrap", md: "nowrap" },
                    },
                    "& .MuiTablePagination-select": {
                      fontSize: { xs: "0.75rem", md: "0.875rem" },
                    },
                  }}
                />
              )}
          </Box>
        </CardContent>
      </Card>

      {renderAnnouncementModal({
        open: isCreateModalOpen,
        onClose: handleCloseCreateModal,
        title: "Crear nuevo anuncio",
        formState: createForm,
        setFormState: setCreateForm,
        formClientOptions: createClientOptions,
        error: formError,
        setError: (message) => {
          setFormError(message);
          setCreateErrorStep(STEP_AUDIENCE);
        },
        errorStep: createErrorStep,
        step: createStep,
        setStep: setCreateStep,
        isBusy: isSubmitting,
        onSubmit: handleCreateAnnouncement,
        submitLabel: "Crear anuncio",
        busyLabel: "Creando...",
        isEdit: false,
      })}

      {isSuperAdmin &&
        renderAnnouncementModal({
          open: isEditModalOpen,
          onClose: handleCloseEditModal,
          title: "Editar anuncio",
          formState: editForm,
          setFormState: setEditForm,
          formClientOptions: editClientOptions,
          error: editFormError,
          setError: (message) => {
            setEditFormError(message);
            setEditErrorStep(STEP_AUDIENCE);
          },
          errorStep: editErrorStep,
          step: editStep,
          setStep: setEditStep,
          isBusy: isUpdatingAnnouncement,
          onSubmit: handleUpdateAnnouncement,
          submitLabel: "Guardar cambios",
          busyLabel: "Guardando...",
          isEdit: true,
        })}

      <AnnouncementDetailModal
        open={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setDeleteState({ isDeleting: false, error: null });
        }}
        announcement={selectedAnnouncement}
        userRole={currentUser?.role}
        onEdit={
          isSuperAdmin
            ? () => {
              setAgenciesRefreshKey((prev) => prev + 1);
              handleOpenEditModal();
            }
            : undefined
        }
        onDelete={isSuperAdmin ? () => setConfirmDeleteOpen(true) : undefined}
        isDeleting={deleteState.isDeleting}
        deleteError={deleteState.error}
      />

      <ConfirmModal
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={async () => {
          await handleDeleteAnnouncement();
          setConfirmDeleteOpen(false);
        }}
        title="Eliminar anuncio"
        description={
          <>
            ¿Estás seguro de que quieres eliminar el anuncio{" "}
            <strong>{selectedAnnouncement?.title}</strong>? Esta acción no se puede deshacer.
          </>
        }
        confirmLabel="Sí, eliminar"
        loadingLabel="Eliminando..."
        loading={deleteState.isDeleting}
        tone="danger"
      />

      <SuccessPopup
        message={successPopup.message}
        isOpen={successPopup.isOpen}
        onClose={handleCloseSuccessPopup}
        error={successPopup.error}
      />
    </>
  );
});
